/**
 * CollabInvitations – modal som lar skaper opprette delbare lenker for å
 * invitere eksterne (uten konto) til å se kalenderen sin.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Copy, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export default function CollabInvitations({ onClose }) {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [label, setLabel] = useState("");
  const [durationMonths, setDurationMonths] = useState(1);
  const [creating, setCreating] = useState(false);

  const load = () => {
    api.get("/collab/invitations").then((r) => setItems(r.data?.results || [])).catch(() => setItems([]));
  };
  useEffect(load, []);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.post("/collab/invitations", { label: label.trim() || null, duration_months: durationMonths });
      setItems([r.data, ...items]);
      setLabel("");
      setDurationMonths(1);
      toast.success(t("collab.invitation_created"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm(t("collab.confirm_delete_invite"))) return;
    await api.delete(`/collab/invitations/${id}`);
    setItems(items.filter((x) => x.id !== id));
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/samarbeid/invitasjon/${token}`;
    navigator.clipboard.writeText(url).then(() => toast.success(t("collab.link_copied")));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="invite-modal">
      <div className="max-w-xl w-full panel-tight p-6 relative my-8" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-2">{t("collab.invite_title")}</h3>
        <p className="text-sm text-[var(--cream-soft)] mb-4">{t("collab.invite_subtitle")}</p>

        <div className="flex flex-col sm:flex-row gap-2 mb-6">
          <input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={80}
            placeholder={t("collab.invite_label_placeholder")}
            className="input-editorial flex-1" data-testid="invite-label" />
          <select value={durationMonths} onChange={(e) => setDurationMonths(parseInt(e.target.value, 10))}
            className="input-editorial sm:w-32" data-testid="invite-duration">
            {[1,2,3,6,9,12].map((m) => (
              <option key={m} value={m}>{m} {m === 1 ? "mnd" : "mnd"}</option>
            ))}
          </select>
          <button onClick={create} disabled={creating} className="btn-magenta inline-flex items-center justify-center gap-1.5"
            data-testid="invite-create">
            <Plus className="h-3.5 w-3.5" /> {t("collab.create")}
          </button>
        </div>

        {items.length === 0 ? (
          <div className="text-sm text-[var(--cream-faint)] italic">{t("collab.no_invitations")}</div>
        ) : (
          <ul className="space-y-2">
            {items.map((inv) => (
              <li key={inv.id} className="border border-white/10 p-3 flex items-center gap-3" data-testid={`invite-${inv.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-meta text-xs text-[var(--cream)] truncate">{inv.label || t("collab.no_label")}</div>
                  <div className="font-meta text-[10px] text-[var(--cream-faint)] truncate">
                    {window.location.origin}/samarbeid/invitasjon/{inv.token}
                  </div>
                  <div className="font-meta text-[10px] text-[var(--cream-faint)] mt-0.5">
                    {t("collab.uses_count", { count: inv.uses || 0 })} · {t("collab.expires_at", { date: new Date(inv.expires_at).toLocaleDateString("nb-NO") })}
                  </div>
                </div>
                <button onClick={() => copyLink(inv.token)} className="text-[var(--magenta)] hover:text-[var(--gold)] p-1" data-testid={`invite-copy-${inv.id}`}>
                  <Copy className="h-4 w-4" />
                </button>
                <button onClick={() => remove(inv.id)} className="text-red-300 hover:text-red-200 p-1">
                  <Trash2 className="h-4 w-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
